export type MatchStatus = "matched" | "unmatched_rec" | "unmatched_send";

export const CAMPAIGN_GROUPS = {
  THIS_FLOW: "this_flow",
  CUSTOMER_MANAGED: "customer_managed",
  OTHER_FLOWS: "other_flows",
  OTHER_FLOWS_CUSTOMER_MANAGED: "other_flows_customer_managed",
  UNACCOUNTED: "unaccounted",
  NULL_CAMPAIGN: "null_campaign",
} as const;

export type CampaignGroup =
  (typeof CAMPAIGN_GROUPS)[keyof typeof CAMPAIGN_GROUPS];

/**
 * Priority order for assigning campaign IDs to groups.
 * Used by both buildCampaignGroupMap and the SQL CASE expression.
 */
export const CAMPAIGN_GROUP_PRIORITY: CampaignGroup[] = [
  CAMPAIGN_GROUPS.THIS_FLOW,
  CAMPAIGN_GROUPS.CUSTOMER_MANAGED,
  CAMPAIGN_GROUPS.OTHER_FLOWS,
  CAMPAIGN_GROUPS.OTHER_FLOWS_CUSTOMER_MANAGED,
];

export interface FlowCampaignInfo {
  flowMessageCampaignIds: string[];
  customerManagedCampaignIds?: string[];
}

export interface ExperimentUserRow {
  experiment: string;
  send_date: string;
  rolling_30d_users: number;
}

export interface SendInsightRow {
  send_date: string;
  experiment: string;
  campaign_group: CampaignGroup;
  channel: string;
  channel_type: string;
  message_id: string | null;
  day_of_week: number;
  time_bucket: string;
  match_status: MatchStatus;
  row_count: number;
  [key: `metadata_${string}`]: number;
}

export type SendInsightDimension =
  | "none"
  | "channel_type"
  | "channel"
  | "experiment"
  | "message_id"
  | "campaign_group"
  | "match_status"
  | "day_of_week"
  | "time_bucket";

export type MetricNumerator =
  | "matched"
  | "sends"
  | "recs"
  | "rolling_7d_channel_reach"
  | `metadata.${string}`;

export type MetricDenominator = "sends" | "recs" | "rolling_30d_users" | "none";

export interface ChartFilters {
  experiments?: string[];
  campaign_groups?: CampaignGroup[];
  channel_types?: string[];
}

export interface ChartConfig {
  id: string;
  name: string;
  description?: string;
  type: "line" | "grid" | "channel-reach-line";
  numerator: MetricNumerator;
  denominator: MetricDenominator;
  segment_by: SendInsightDimension;
  segment_by_2?: SendInsightDimension;
  filters: ChartFilters;
  source: "system" | "user";
  is_deleted?: boolean;
  window_days?: number;
  rolling_window_days?: number;
  downloadable?: boolean;
}

export interface FlowSendInsightViews {
  fidelity_charts: ChartConfig[];
  comparison_charts: ChartConfig[];
}

export interface LineChartPoint {
  send_date: string;
  value: number;
  numerator: number;
  denominator?: number | null;
}

export interface LineChartSeries {
  segments: Record<string, string | number>;
  points: LineChartPoint[];
}

export interface LineChartResult {
  type: "line";
  id: string;
  series: LineChartSeries[];
}

export interface GridChartCell {
  segments: Record<string, string | number>;
  value: number | null;
  numerator: number | null;
  denominator: number | null;
}

export interface GridChartResult {
  type: "grid";
  id: string;
  cells: GridChartCell[];
}

export interface RollingChannelUserCountRow {
  send_date: string;
  channel: string;
  rolling_7d_unique_scheduled: number;
  rolling_7d_unique_delivered: number;
}

export type ChartResult = LineChartResult | GridChartResult;

export interface ComputeChartsResult {
  charts: ChartResult[];
  /** Anchor the server windows charts against, as `yyyy-MM-dd`. */
  latestSendDate?: string | null;
}
